<template>
    <div class="bg-stage-background flex-1 relative overflow-y-auto">
        <loading :status="loading.form"/>
        <form @submit.prevent="saveSettings">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="mx-auto max-w-5xl w-full flex items-center justify-between gap-4">
                    <div class="min-w-0 flex-1">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">Position Einstellungen</h1>
                    </div>
                    <div class="flex items-center">
                        <router-link class="btn-stage-secondary" :to="'/workspace/positions/' + $route.params.id + '/manage'">
                            Abbrechen
                        </router-link>
                        <button :disabled="!position.can_edit" class="btn-stage-primary ml-3" type="submit">
                            Speichern
                        </button>
                    </div>
                </div>
            </div>

            <div class="px-8 sm:px-8 lg:px-8 py-6">
                <div class="mx-auto max-w-5xl">
                    <div class="bg-white shadow sm:rounded overflow-hidden border border-bordergray-100">
                        <div class="md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                            <div class="md:col-span-1">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">Prozessschritte</h3>
                                <p class="mt-1 text-sm leading-5 text-gray-500">
                                    Start und Abschluss sind verpflichtend und können nicht deaktiviert werden.
                                    Für diese beiden Schritte wird jeweils nur ein Datum gesetzt (Start- und Enddatum identisch).
                                    Für alle anderen Schritte sind optionale Zeiträume mit Start- und Enddatum möglich.
                                </p>
                            </div>
                            <div class="mt-5 md:mt-0 md:col-span-2">
                                <div v-if="visibleProcessSteps.length > 0" class="space-y-6">
                                    <div
                                        v-for="processStep in visibleProcessSteps"
                                        :key="'settings-position-dates-' + processStep.id"
                                        :style="stepCardStyle(processStep)"
                                        class="border border-bordergray-100 rounded bg-white overflow-hidden"
                                    >
                                        <div class="w-full px-4 py-3 text-left flex items-center">
                                            <div v-if="isMandatoryBoundaryStep(processStep)" class="inline-flex items-center min-w-0">
                                                <span class="min-w-0">
                                                    <span class="block font-medium text-gray-900 truncate">{{ processStep.name }}</span>
                                                    <span v-if="processStep.short_description" class="block text-sm text-gray-500 truncate">
                                                        {{ processStep.short_description }}
                                                    </span>
                                                </span>
                                            </div>
                                            <label v-else class="inline-flex items-center min-w-0 cursor-pointer">
                                                <input
                                                    :checked="isSelected(processStep.id)"
                                                    class="form-checkbox h-4 w-4 text-gray-600"
                                                    type="checkbox"
                                                    :disabled="!position.can_edit"
                                                    @change="onProcessStepToggle(processStep.id, $event.target.checked)"
                                                >
                                                <span class="ml-3 min-w-0">
                                                    <span class="block font-medium text-gray-900 truncate">{{ processStep.name }}</span>
                                                    <span v-if="processStep.short_description" class="block text-sm text-gray-500 truncate">
                                                        {{ processStep.short_description }}
                                                    </span>
                                                </span>
                                            </label>
                                        </div>

                                        <div v-show="isSelected(processStep.id)" class="px-4 pb-4 border-t border-bordergray-100">
                                            <div v-if="!isMandatoryBoundaryStep(processStep)" class="mb-4 mt-3">
                                                <label class="stage-filter-label block mb-1">Mitarbeiterzuweisung</label>
                                                <div class="border border-bordergray-100 rounded bg-white">
                                                    <template v-if="position.step_reader_options.length > 0">
                                                        <label
                                                            v-for="employee in position.step_reader_options"
                                                            :key="'position-settings-step-reader-' + processStep.id + '-' + employee.id"
                                                            class="flex items-center px-3 py-2 border-b border-bordergray-100 last:border-b-0 hover:bg-gray-100 cursor-pointer"
                                                        >
                                                            <input
                                                                v-model="stepConfiguration[processStep.id].reader_ids"
                                                                :disabled="!position.can_edit || !isSelected(processStep.id)"
                                                                :value="employee.id"
                                                                class="form-checkbox h-4 w-4 text-gray-600"
                                                                type="checkbox"
                                                            >
                                                            <span class="ml-2 text-sm text-gray-800">{{ employee.name }}</span>
                                                        </label>
                                                    </template>
                                                    <div v-else class="px-3 py-3 text-sm text-gray-500">Keine Mitarbeiter verfügbar</div>
                                                </div>
                                            </div>
                                            <div class="mb-4" :class="isMandatoryBoundaryStep(processStep) ? 'mt-3' : ''">
                                                <div v-if="isMandatoryBoundaryStep(processStep)" class="mt-4">
                                                    <label class="stage-filter-label block mb-1">Datum</label>
                                                    <input
                                                        v-model="stepConfiguration[processStep.id].single_date"
                                                        :disabled="!position.can_edit || !isSelected(processStep.id)"
                                                        class="stage-filter-input form-input block w-full text-sm rounded"
                                                        type="date"
                                                    >
                                                </div>
                                                <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                                                    <div>
                                                        <label class="stage-filter-label block mb-1">Startdatum</label>
                                                        <input
                                                            v-model="stepConfiguration[processStep.id].start_date"
                                                            :disabled="!position.can_edit || !isSelected(processStep.id)"
                                                            class="stage-filter-input form-input block w-full text-sm rounded"
                                                            type="date"
                                                        >
                                                    </div>
                                                    <div>
                                                        <label class="stage-filter-label block mb-1">Enddatum</label>
                                                        <input
                                                            v-model="stepConfiguration[processStep.id].end_date"
                                                            :disabled="!position.can_edit || !isSelected(processStep.id)"
                                                            class="stage-filter-input form-input block w-full text-sm rounded"
                                                            type="date"
                                                        >
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="text-sm text-gray-500">Keine Prozessschritte verfügbar</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>

    </div>
</template>

<script>
import {UI_COLORS} from '@/utils/ui-colors';

export default {
    name: "position-settings",
    metaInfo() {
        return {
            title: "Position Einstellungen"
        }
    },
    computed: {
        visibleProcessSteps() {
            const categoryId = Number(this.position.process_step_category_id || 0);
            if (categoryId < 1) {
                return this.allProcessSteps || [];
            }

            return (this.allProcessSteps || []).filter((step) => {
                return Number(step.process_step_category_id || 0) === categoryId;
            });
        },
        mandatoryStepIds() {
            return (this.visibleProcessSteps || [])
                .filter((step) => this.isMandatoryBoundaryStep(step))
                .map((step) => Number(step.id))
                .filter((id) => id > 0);
        },
    },
    data() {
        return {
            loading: {
                form: false,
            },
            allProcessSteps: [],
            selectedProcessStepIds: [],
            stepConfiguration: {},
            position: {
                title: '',
                body: '',
                attachments: [],
                current_process_step_id: null,
                process_step_category_id: null,
                can_edit: false,
                can_manage_permissions: false,
                step_reader_options: [],
            },
        };
    },
    mounted() {
        this.getFilters();
        this.getPosition();
    },
    methods: {
        getFilters() {
            const self = this;
            axios.get('api/workspace/positions/filters').then(function (response) {
                self.allProcessSteps = response.data.process_steps || [];
                self.ensureStepConfiguration();
                self.normalizeStepSelection();
            });
        },
        getPosition() {
            const self = this;
            self.loading.form = true;
            axios.get('api/workspace/positions/' + self.$route.params.id).then(function (response) {
                self.position = response.data;
                const configuredSteps = self.position.process_steps || [];
                self.selectedProcessStepIds = configuredSteps.map((step) => step.id);
                self.stepConfiguration = {};
                configuredSteps.forEach((step) => {
                    const singleDate = step.start_date || step.end_date || null;
                    self.stepConfiguration[step.id] = {
                        start_date: step.start_date || null,
                        end_date: step.end_date || null,
                        single_date: singleDate,
                        body: step.body || null,
                        is_completed: step.is_completed || false,
                        reader_ids: Array.isArray(step.reader_ids) ? [...step.reader_ids] : [],
                    };
                });
                self.ensureStepConfiguration();
                self.normalizeStepSelection();
                self.loading.form = false;
            }).catch(function () {
                self.loading.form = false;
                self.$router.push('/workspace/positions');
            });
        },
        ensureStepConfiguration() {
            (this.allProcessSteps || []).forEach((step) => {
                const stepId = step.id;
                if (!this.stepConfiguration[stepId]) {
                    this.stepConfiguration[stepId] = {
                        start_date: null,
                        end_date: null,
                        single_date: null,
                        body: null,
                        is_completed: false,
                        reader_ids: [],
                    };
                }
                if (!Array.isArray(this.stepConfiguration[stepId].reader_ids)) {
                    this.stepConfiguration[stepId].reader_ids = [];
                }
                if (typeof this.stepConfiguration[stepId].single_date === 'undefined') {
                    this.stepConfiguration[stepId].single_date = this.stepConfiguration[stepId].start_date
                        || this.stepConfiguration[stepId].end_date
                        || null;
                }
            });
        },
        onProcessStepToggle(stepId, isChecked) {
            const numericStepId = Number(stepId);
            const processStep = (this.visibleProcessSteps || []).find((step) => Number(step.id) === numericStepId);
            if (processStep && this.isMandatoryBoundaryStep(processStep) && !isChecked) {
                return;
            }
            const selectedIds = new Set((this.selectedProcessStepIds || []).map((id) => Number(id)));

            if (isChecked) {
                selectedIds.add(numericStepId);
            } else {
                selectedIds.delete(numericStepId);
            }

            this.selectedProcessStepIds = Array.from(selectedIds);
            this.ensureStepConfiguration();

            if (!isChecked && this.stepConfiguration[numericStepId]) {
                this.stepConfiguration[numericStepId].start_date = null;
                this.stepConfiguration[numericStepId].end_date = null;
                this.stepConfiguration[numericStepId].single_date = null;
                this.stepConfiguration[numericStepId].reader_ids = [];
            }

            this.normalizeStepSelection();
        },
        normalizeStepSelection() {
            const visibleIds = new Set((this.visibleProcessSteps || []).map((step) => Number(step.id)));
            const selectedIds = (this.selectedProcessStepIds || [])
                .map((id) => Number(id))
                .filter((id) => visibleIds.has(id));

            this.selectedProcessStepIds = Array.from(new Set([
                ...selectedIds,
                ...this.mandatoryStepIds,
            ]));
        },
        isSelected(stepId) {
            return (this.selectedProcessStepIds || []).map((id) => Number(id)).includes(Number(stepId));
        },
        isMandatoryBoundaryStep(processStep) {
            if (!processStep) {
                return false;
            }

            const key = (processStep.system_key || '').toString();
            return key.endsWith('_process_start') || key.endsWith('_process_completion');
        },
        stepCardStyle(processStep) {
            const color = processStep && processStep.color ? processStep.color : UI_COLORS.border.default;
            return {
                borderLeftWidth: '4px',
                borderLeftColor: color,
            };
        },
        saveSettings() {
            if (!this.position.can_edit) {
                return;
            }

            const dateValidationError = this.validateProcessStepDates();
            if (dateValidationError) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: dateValidationError,
                    type: 'error'
                });
                return;
            }

            const payload = {
                title: this.position.title,
                body: this.position.body,
                attachments: this.position.attachments || [],
                process_step_ids: this.selectedProcessStepIds || [],
                process_steps: (this.selectedProcessStepIds || []).map((stepId) => {
                    const processStep = (this.visibleProcessSteps || []).find((step) => Number(step.id) === Number(stepId));
                    const config = this.stepConfiguration[stepId] || {};
                    const isMandatoryStep = this.isMandatoryBoundaryStep(processStep);
                    const singleDate = (config.single_date || config.start_date || config.end_date || null);

                    return {
                        id: stepId,
                        start_date: isMandatoryStep ? singleDate : (config.start_date || null),
                        end_date: isMandatoryStep ? singleDate : (config.end_date || null),
                        body: null,
                        is_completed: !!config.is_completed,
                        reader_ids: isMandatoryStep ? [] : (Array.isArray(config.reader_ids) ? config.reader_ids : []),
                    };
                }),
            };

            const self = this;
            self.loading.form = true;
            axios.put('api/workspace/positions/' + self.$route.params.id, payload).then(function () {
                self.loading.form = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/workspace/positions/' + self.$route.params.id + '/manage');
            }).catch(function () {
                self.loading.form = false;
            });
        },
        validateProcessStepDates() {
            for (const stepId of (this.selectedProcessStepIds || [])) {
                const config = this.stepConfiguration[stepId] || {};
                const processStep = (this.visibleProcessSteps || []).find((step) => Number(step.id) === Number(stepId));
                const isMandatoryStep = this.isMandatoryBoundaryStep(processStep);
                const startDate = (config.start_date || '').toString().trim();
                const endDate = (config.end_date || '').toString().trim();
                const hasStartDate = startDate !== '';
                const hasEndDate = endDate !== '';
                const singleDate = (config.single_date || '').toString().trim();

                if (isMandatoryStep) {
                    if (singleDate === '') {
                        continue;
                    }
                    if (Number.isNaN(Date.parse(singleDate))) {
                        return this.$t('The selected :attribute is invalid', {attribute: this.$t('date')}).toString() + '.';
                    }
                    continue;
                }

                if (hasStartDate !== hasEndDate) {
                    return this.$t('Start and end date must be set together for each process step').toString() + '.';
                }

                if (!hasStartDate && !hasEndDate) {
                    continue;
                }

                if (endDate < startDate) {
                    return this.$t('The end date must not be before the start date').toString() + '.';
                }
            }

            return null;
        },
    }
}
</script>
